import React, { useEffect, useMemo, useState } from 'react';
import BasicCli from '../../main/components/basicCli/BasicCli';
import { ApiConstants } from '../../main/constants/ApiConstants';
import { IPlaygroundSession } from '../../main/interfaces/IPlayground';
import IUser from '../../main/interfaces/IUser';
import PrintIcon from '@mui/icons-material/Print';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import axiosInstance from '../../main/initializers/axios';
import { useTranslation } from 'react-i18next';
import useGetUser from '../../main/hooks/useGetUser';
import { usePerformAction } from '../../main/store/stores/user/permission.store';
import '../umb/umbsManagement/UmbsManagement.css';
import '../umb/umbs/userUmbrellaInventory/UserUmbrellaInventory.css';

interface PriceSummaryItem {
  count: number;
  price: number;
}

const getPriceSummary = (inventory: IPlaygroundSession[]): PriceSummaryItem[] => {
  const countByPrice = new Map<number, number>();

  inventory.forEach((item) => {
    const price = Number(item.price || 0);
    countByPrice.set(price, (countByPrice.get(price) || 0) + 1);
  });

  return Array.from(countByPrice, ([price, count]) => ({ price, count })).sort((a, b) => b.price - a.price);
};

const isClosedSession = (item: IPlaygroundSession): boolean => {
  if (item.isActive === false) return true;
  if (!item.endTime) return false;

  const endTime = new Date(item.endTime).getTime();
  return !Number.isNaN(endTime) && endTime <= Date.now();
};

const UserPlaygroundInventory = () => {
  const { t } = useTranslation();
  const currentUser = useGetUser();
  const [users, setUsers] = useState<IUser[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number>(0);
  const [inventory, setInventory] = useState<IPlaygroundSession[]>([]);
  const [dateSelected, setDateSelected] = useState<string>(new Date().toISOString());
  const [closed, setClosed] = useState<boolean>(false);
  const canCloseInventory = usePerformAction('playground/closeInventoryInvoice', true);

  const userDisplayName = useMemo(() => {
    const selectedUser = users.find((user) => user.id === selectedUserId) ?? (currentUser?.id === selectedUserId ? currentUser : undefined);
    const fullName = `${selectedUser?.firstName ?? ''} ${selectedUser?.lastName ?? ''}`.trim();
    return fullName || selectedUser?.userName || selectedUser?.email || t('playgroundInventory.user', { defaultValue: 'User' });
  }, [currentUser, selectedUserId, t, users]);

  const getUsers = () => {
    axiosInstance
      .get(ApiConstants.User.users)
      .then((response) => {
        const loadedUsers = (response.data ?? []) as IUser[];
        if (currentUser && !loadedUsers.some((user) => user.id === currentUser.id)) {
          loadedUsers.unshift(currentUser);
        }
        setUsers(loadedUsers);
      })
      .catch((error) => {
        if (currentUser) {
          setUsers([currentUser]);
        }
        console.error(t('errors.fetchUsers'), error);
      });
  };

  const getInventory = (userId: number, date: Date, isClosed: boolean) => {
    if (!userId) {
      setInventory([]);
      return;
    }

    axiosInstance
      .post(ApiConstants.playground.inventory, {
        userId,
        date,
        closed: isClosed,
      })
      .then((response) => {
        const items = (response.data ?? []) as IPlaygroundSession[];
        setInventory(items.filter((item) => (isClosed ? isClosedSession(item) : !isClosedSession(item))));
      })
      .catch((error) => {
        console.error(t('playgroundInventory.messages.loadError', { defaultValue: 'Error fetching playground inventory' }), error);
      });
  };

  useEffect(() => {
    getUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!currentUser?.id || selectedUserId !== 0) return;
    setSelectedUserId(currentUser.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id]);

  useEffect(() => {
    getInventory(selectedUserId, new Date(dateSelected), closed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUserId]);

  const handleUserChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedUserId(Number(event.target.value));
  };

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.validity.valid) return;
    const dt = event.target.value + ':00Z';
    setDateSelected(dt);
    getInventory(selectedUserId, new Date(dt), closed);
  };

  const handleClosedInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isClosed = event.target.checked;
    setClosed(isClosed);
    getInventory(selectedUserId, new Date(dateSelected), isClosed);
  };

  const closeInvoice = () => {
    if (!selectedUserId) return;

    axiosInstance
      .post(ApiConstants.playground.closeInvoice, {
        userId: selectedUserId,
        date: new Date(dateSelected),
        closed,
      })
      .then(() => {
        getInventory(selectedUserId, new Date(dateSelected), closed);
      })
      .catch((error) => {
        console.error(t('playgroundInventory.messages.closeError', { defaultValue: 'Error closing playground inventory' }), error);
      });
  };

  const formatTime = (val: string | undefined | null): string => {
    if (!val) return '';
    const date = new Date(val);
    if (Number.isNaN(date.getTime())) return '';
    return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  };

  const formatPrice = (val: number | undefined | null): string => Number(val || 0).toFixed(2);

  const formatDateForDisplay = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('sq-AL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const truncateText = (text: string | undefined, length: number) => {
    if (text == null || text === '') return '';
    return text.substring(0, length);
  };

  const sortedInventory = useMemo(() => {
    return [...inventory].sort((a, b) => {
      const playgroundCompare = (a.playgroundName || '').localeCompare(b.playgroundName || '', undefined, { numeric: true });
      if (playgroundCompare !== 0) return playgroundCompare;
      return new Date(a.startTime || 0).getTime() - new Date(b.startTime || 0).getTime();
    });
  }, [inventory]);

  const total = useMemo(() => sortedInventory.reduce((sum, item) => sum + (item.price || 0), 0), [sortedInventory]);
  const priceSummary = useMemo(() => getPriceSummary(sortedInventory), [sortedInventory]);

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html lang="sq">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>${t('playgroundInventory.report.windowTitle', { defaultValue: 'Playground inventory report' })}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; color: #000; }
          .print-header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #000; padding-bottom: 15px; }
          .print-header h1 { font-size: 24px; margin: 0; color: #000; }
          .print-header .info { margin-top: 10px; font-size: 14px; color: #555; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th { background-color: #f2f2f2; padding: 12px; border: 1px solid #000; font-weight: bold; text-align: left; }
          td { padding: 10px; border: 1px solid #000; }
          .total-section { margin-top: 30px; text-align: right; font-weight: bold; font-size: 18px; border-top: 2px solid #000; padding-top: 15px; }
          .price-breakdown { margin-top: 8px; font-size: 15px; font-weight: normal; line-height: 1.6; }
        </style>
      </head>
      <body>
        <div class="print-header">
          <h1>${t('playgroundInventory.report.title', { defaultValue: 'PLAYGROUND INVENTORY REPORT' })}</h1>
          <div class="info">
            <strong>${t('playgroundInventory.report.employee', { defaultValue: 'User' })}:</strong> ${userDisplayName}<br>
            <strong>${t('playgroundInventory.report.date', { defaultValue: 'Date' })}:</strong> ${formatDateForDisplay(dateSelected)}<br>
            <strong>${t('playgroundInventory.report.status', { defaultValue: 'Status' })}:</strong> ${closed ? t('playgroundInventory.report.closed', { defaultValue: 'Closed' }) : t('playgroundInventory.report.open', { defaultValue: 'Open' })}
          </div>
        </div>
        <table>
          <thead>
            <tr>
              <th>${t('playgroundInventory.table.playground', { defaultValue: 'Playground' })}</th>
              <th>${t('playgroundInventory.table.child', { defaultValue: 'Child' })}</th>
              <th>${t('playgroundInventory.table.open', { defaultValue: 'Open' })}</th>
              <th>${t('playgroundInventory.table.close', { defaultValue: 'Close' })}</th>
              <th>${t('playgroundInventory.table.note', { defaultValue: 'Note' })}</th>
              <th style="text-align: right;">${t('playgroundInventory.table.price', { defaultValue: 'Price' })}</th>
            </tr>
          </thead>
          <tbody>
            ${sortedInventory
              .map(
                (item) => `
                <tr>
                  <td>${item.playgroundName || '-'}</td>
                  <td>${item.childName || '-'}</td>
                  <td>${formatTime(item.startTime)}</td>
                  <td>${formatTime(item.endTime)}</td>
                  <td>${item.notes || ''}</td>
                  <td style="text-align: right;">${formatPrice(item.price)}</td>
                </tr>
              `,
              )
              .join('')}
          </tbody>
        </table>
        <div class="total-section">
          ${t('playgroundInventory.report.total', { defaultValue: 'Total' })}: ${formatPrice(total)}
          <div class="price-breakdown">
            ${priceSummary.map((item) => `<div>${item.count} x ${formatPrice(item.price)} = ${formatPrice(item.count * item.price)}</div>`).join('')}
          </div>
        </div>
      </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const exportToPDF = () => {
    alert(t('playgroundInventory.messages.pdfPlaceholder', { defaultValue: 'PDF export will be implemented later. For now, please use Print.' }));
  };

  return (
    <BasicCli>
      <div className="umbmPage">
        <div className="ui-panel umbMToolbar">
          <div className="umbMFilters userUmbFilters">
            <select
              className="ui-control ui-select userUmbUserSelect"
              value={selectedUserId}
              onChange={handleUserChange}
              aria-label={t('invoice.optionSelectEmployee')}
            >
              <option value={0}>{t('invoice.optionSelectEmployee')}</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {`${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.userName || user.email}
                </option>
              ))}
            </select>

            <div className="userUmbDate">
              <input
                type="datetime-local"
                className="ui-control ui-input"
                value={dateSelected.toString().substring(0, 16)}
                onChange={handleDateChange}
              />
            </div>

            <label className="umbMCheck userUmbClosed">
              <input type="checkbox" className="ui-check" checked={closed} onChange={handleClosedInput} />
              <span>{t('playgroundInventory.closed', { defaultValue: 'Closed' })}</span>
            </label>

            <div className="umbMActions">
              <button className="ui-control ui-btn ui-btn--primary" onClick={handlePrint}>
                <PrintIcon fontSize="small" />
                {t('playgroundInventory.print', { defaultValue: 'Print' })}
              </button>

              <button className="ui-control ui-btn ui-btn--danger" onClick={exportToPDF}>
                <PictureAsPdfIcon fontSize="small" />
                {t('playgroundInventory.pdf', { defaultValue: 'PDF' })}
              </button>
            </div>
          </div>
        </div>

        <div className="umbMGrid">
          <div className="bcCard umbMTableCard">
            <div className="umbMTableScroll">
              <table className="bcTable">
                <thead>
                  <tr>
                    <th>{t('playgroundInventory.table.playground', { defaultValue: 'Playground' })}</th>
                    <th>{t('playgroundInventory.table.child', { defaultValue: 'Child' })}</th>
                    <th>{t('playgroundInventory.table.open', { defaultValue: 'Open' })}</th>
                    <th>{t('playgroundInventory.table.close', { defaultValue: 'Close' })}</th>
                    <th>{t('playgroundInventory.table.note', { defaultValue: 'Note' })}</th>
                    <th style={{ textAlign: 'right' }}>{t('playgroundInventory.table.price', { defaultValue: 'Price' })}</th>
                  </tr>
                </thead>

                <tbody>
                  {sortedInventory.length > 0 ? (
                    sortedInventory.map((item) => (
                      <tr key={item.id}>
                        <td style={{ fontWeight: 600 }}>{item.playgroundName || '-'}</td>
                        <td>{item.childName || '-'}</td>
                        <td>{formatTime(item.startTime)}</td>
                        <td>{formatTime(item.endTime)}</td>
                        <td title={item.notes}>{truncateText(item.notes, 30)}</td>
                        <td style={{ textAlign: 'right', fontWeight: 700, color: 'var(--success)' }}>{formatPrice(item.price)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="umbMEmpty">
                        <h4>{t('playgroundInventory.empty.title', { defaultValue: 'No data' })}</h4>
                        <p>{selectedUserId === 0 ? t('invoice.optionSelectEmployee') : t('playgroundInventory.empty.message', { defaultValue: 'No data for this period' })}</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bcCard umbMSide">
            <div className="umbMSideTop">
              <div>
                <h4 className="umbMTitle">{userDisplayName}</h4>
                <small className="bcMuted">{formatDateForDisplay(dateSelected)}</small>
              </div>
            </div>

            <hr className="umbMDivider" />

            <div className="umbMSummary">
              <div className="umbMRow">
                <span className="bcMuted">{t('playgroundInventory.summary.totalItems', { defaultValue: 'Total items:' })}</span>
                <strong>{sortedInventory.length}</strong>
              </div>

              <div className="umbMRow">
                <span style={{ fontWeight: 700 }}>{t('playgroundInventory.summary.total', { defaultValue: 'Total:' })}</span>
                <div className="umbMTotal">{formatPrice(total)}</div>
              </div>

              <div className="umbMPriceSummary">
                {priceSummary.map((item) => (
                  <div key={item.price}>
                    {item.count} x {formatPrice(item.price)} = {formatPrice(item.count * item.price)}
                  </div>
                ))}
              </div>
            </div>

            <hr className="umbMDivider" />
            {canCloseInventory && (
              <button className="ui-control ui-btn ui-btn--primary" onClick={closeInvoice} disabled={selectedUserId === 0}>
                {t('playgroundInventory.closeInvoice', { defaultValue: 'Close invoice' })}
              </button>
            )}
            <div className="umbMStatus">
              <small className="bcMuted">
                {t('playgroundInventory.summary.status', { defaultValue: 'Status:' })}{' '}
                <span style={{ color: closed ? 'var(--success)' : 'var(--warning)', fontWeight: 800 }}>
                  {closed ? t('playgroundInventory.summary.closed', { defaultValue: 'CLOSED' }) : t('playgroundInventory.summary.open', { defaultValue: 'OPEN' })}
                </span>
              </small>
            </div>
          </div>
        </div>
      </div>
    </BasicCli>
  );
};

export default UserPlaygroundInventory;
